import { useState, useEffect } from 'react';
import { Shield, AlertTriangle, CheckCircle2, XCircle, Loader2, ExternalLink } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';

interface SteamSecurityStatus {
  inventory_public: boolean;
  steam_guard_enabled: boolean;
  steam_guard_days?: number;
  can_trade: boolean;
  errors: string[];
}

interface SteamSecurityCheckProps {
  steamId: string;
}

export function SteamSecurityCheck({ steamId }: SteamSecurityCheckProps) {
  const [status, setStatus] = useState<SteamSecurityStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    checkSecurity();
  }, [steamId]);

  const checkSecurity = async () => {
    try {
      setLoading(true);
      // В реальном приложении здесь будет API запрос
      // const response = await fetch(`/api/v1/steam/security-check?steam_id=${steamId}`);
      // const data = await response.json();
      
      // Имитация проверки
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setStatus({
        inventory_public: true,
        steam_guard_enabled: true,
        steam_guard_days: 15,
        can_trade: true,
        errors: []
      });
    } catch (err) {
      setError('Не удалось проверить статус безопасности');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-[#1a2332] rounded-lg p-6 border border-[#30363d]">
        <div className="flex items-center gap-3 text-[#8b949e]">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Проверка статуса Steam...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert className="bg-red-500/10 border-red-500/30">
        <XCircle className="w-4 h-4 text-red-500" />
        <AlertDescription className="text-red-400">{error}</AlertDescription>
      </Alert>
    );
  }

  if (!status) return null;

  const allGood = status.can_trade && status.inventory_public && status.steam_guard_enabled;

  return (
    <div className="bg-[#1a2332] rounded-lg p-6 border border-[#30363d] space-y-4">
      <div className="flex items-center gap-3">
        <Shield className={`w-6 h-6 ${allGood ? 'text-green-500' : 'text-yellow-500'}`} />
        <h3 className="text-lg font-semibold text-white">Проверка безопасности</h3>
        {allGood ? (
          <span className="ml-auto px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm">
            Готов к торговле
          </span>
        ) : (
          <span className="ml-auto px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-sm">
            Требуются действия
          </span>
        )}
      </div>

      <div className="space-y-3">
        {/* Inventory Status */}
        <div className="flex items-center justify-between p-3 bg-[#0d1117] rounded-lg">
          <div className="flex items-center gap-3">
            {status.inventory_public ? (
              <CheckCircle2 className="w-5 h-5 text-green-500" />
            ) : (
              <XCircle className="w-5 h-5 text-red-500" />
            )}
            <span className="text-white">Инвентарь публичный</span>
          </div>
          {!status.inventory_public && (
            <a
              href="https://steamcommunity.com/id/me/edit/settings"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-[#66c0f4] hover:underline flex items-center gap-1"
            >
              Исправить
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>

        {/* Steam Guard Status */}
        <div className="flex items-center justify-between p-3 bg-[#0d1117] rounded-lg">
          <div className="flex items-center gap-3">
            {status.steam_guard_enabled ? (
              <CheckCircle2 className="w-5 h-5 text-green-500" />
            ) : (
              <XCircle className="w-5 h-5 text-red-500" />
            )}
            <div>
              <span className="text-white">Steam Guard включен</span>
              {status.steam_guard_days !== undefined && (
                <p className="text-xs text-[#8b949e]">
                  {status.steam_guard_days >= 7 
                    ? `${status.steam_guard_days} дней (готово к обменам)` 
                    : `${status.steam_guard_days} дней (нужно минимум 7)`}
                </p>
              )}
            </div>
          </div>
          {!status.steam_guard_enabled && (
            <a
              href="https://store.steampowered.com/twofactor/setup"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-[#66c0f4] hover:underline flex items-center gap-1"
            >
              Включить
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>

        {/* Trade Hold Status */}
        {status.steam_guard_days !== undefined && status.steam_guard_days < 7 && (
          <Alert className="bg-yellow-500/10 border-yellow-500/30">
            <AlertTriangle className="w-4 h-4 text-yellow-500" />
            <AlertDescription className="text-yellow-400">
              Для торговли необходимо включить Steam Guard минимум 7 дней назад. 
              У вас {status.steam_guard_days} дней. Осталось ждать {7 - status.steam_guard_days} дней.
            </AlertDescription>
          </Alert>
        )}
      </div>

      {status.errors.length > 0 && (
        <div className="space-y-2">
          {status.errors.map((err, idx) => (
            <Alert key={idx} className="bg-red-500/10 border-red-500/30">
              <AlertTriangle className="w-4 h-4 text-red-500" />
              <AlertDescription className="text-red-400">{err}</AlertDescription>
            </Alert>
          ))}
        </div>
      )}

      <Button
        variant="outline"
        onClick={checkSecurity}
        className="w-full border-[#30363d] text-[#8b949e] hover:text-white"
      >
        Обновить статус
      </Button>
    </div>
  );
}
